"use client"

import { useState, useEffect, useCallback } from "react"
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Badge,
  Spinner,
  Alert,
  Input,
  Button,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
} from "reactstrap"
import DataTable, { type TableColumn } from "react-data-table-component"
import { FaArrowLeft, FaPlus, FaTrash, FaSearch, FaSync, FaToggleOn, FaToggleOff, FaUser } from "react-icons/fa"
import { UserRoleService } from "../../../Service/user-role-service"
import type { UserRole, Role } from "../rolemanagement/types"
import { toast } from "react-toastify"
import Swal from "sweetalert2"

interface UserRoleManagementProps {
  userId: number
  onNavigateBack: () => void
}

export default function UserRoleManagement({ userId, onNavigateBack }: UserRoleManagementProps) {
  // State management
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [assignedRoles, setAssignedRoles] = useState<UserRole[]>([])
  const [availableRoles, setAvailableRoles] = useState<Role[]>([])
  const [stats, setStats] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([])
  const [userInfo, setUserInfo] = useState<any>(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [totalRows, setTotalRows] = useState(0)

  // FIXED: Remove userInfo from dependencies to prevent infinite loop
  const fetchData = useCallback(async () => {
    if (!userId) {
      setError("No user specified")
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      console.log(`🔄 Fetching data for user ID: ${userId}`)

      // Fetch data with enhanced error handling
      const [rolesResponse, availableRolesResponse, statsResponse] = await Promise.allSettled([
        UserRoleService.getByUser(userId, { page: currentPage, limit: perPage }),
        UserRoleService.getAvailableRoles(userId),
        UserRoleService.getStats(userId),
      ])

      // Handle roles response
      if (rolesResponse.status === "fulfilled") {
        setAssignedRoles(rolesResponse.value.results || [])
        setTotalRows(rolesResponse.value.count || 0)
        console.log("✅ Successfully fetched user roles:", rolesResponse.value)

        // Extract user info from the first role if available and not already set
        if (rolesResponse.value.results && rolesResponse.value.results.length > 0 && !userInfo) {
          const firstRole = rolesResponse.value.results[0]
          setUserInfo({
            email: firstRole.user_email,
            full_name: firstRole.user_full_name,
          })
        }
      } else {
        console.error("❌ Failed to fetch user roles:", rolesResponse.reason)
        setAssignedRoles([])
        setTotalRows(0)
      }

      // Handle available roles response
      if (availableRolesResponse.status === "fulfilled") {
        setAvailableRoles(availableRolesResponse.value || [])
        console.log("✅ Successfully fetched available roles:", availableRolesResponse.value)
      } else {
        console.error("❌ Failed to fetch available roles:", availableRolesResponse.reason)
        setAvailableRoles([])
      }

      // Handle stats response
      if (statsResponse.status === "fulfilled") {
        setStats(statsResponse.value)
        console.log("✅ Successfully fetched stats:", statsResponse.value)

        // Extract user info from stats if not already set
        if (!userInfo && statsResponse.value) {
          setUserInfo({
            email: statsResponse.value.user_email,
            full_name: statsResponse.value.user_name,
          })
        }
      } else {
        console.error("❌ Failed to fetch stats:", statsResponse.reason)
        setStats({ total_roles: 0, active_roles: 0, inactive_roles: 0, recent_assignments: 0 })
      }
    } catch (error: any) {
      console.error("❌ fetchData error:", error)
      const errorMessage = error?.message || "Failed to load role data"
      setError(errorMessage)
      toast.error(errorMessage)

      // Set empty arrays to prevent further errors
      setAssignedRoles([])
      setAvailableRoles([])
      setTotalRows(0)
    } finally {
      setLoading(false)
    }
  }, [userId, currentPage, perPage]) // REMOVED userInfo from dependencies

  // FIXED: Separate effect for initial load and pagination changes
  useEffect(() => {
    if (userId) {
      fetchData()
    } else {
      setError("No user specified")
      setLoading(false)
    }
  }, [userId, currentPage, perPage, fetchData])

  // Manual refresh function that doesn't depend on state
  const handleRefresh = useCallback(async () => {
    if (!userId) return

    setLoading(true)
    setError(null)

    try {
      const [rolesResponse, availableRolesResponse, statsResponse] = await Promise.allSettled([
        UserRoleService.getByUser(userId, { page: currentPage, limit: perPage }),
        UserRoleService.getAvailableRoles(userId),
        UserRoleService.getStats(userId),
      ])

      if (rolesResponse.status === "fulfilled") {
        setAssignedRoles(rolesResponse.value.results || [])
        setTotalRows(rolesResponse.value.count || 0)
      }

      if (availableRolesResponse.status === "fulfilled") {
        setAvailableRoles(availableRolesResponse.value || [])
      }

      if (statsResponse.status === "fulfilled") {
        setStats(statsResponse.value)
      }

      //toast.success("Data refreshed successfully")
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to refresh data"
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [userId, currentPage, perPage])

  // Handle bulk role assignment
  const handleBulkAssignRoles = async () => {
    if (!userId || selectedRoleIds.length === 0) return

    setSaving(true)
    try {
      const result = await UserRoleService.bulkAssign(userId, selectedRoleIds)

      const message = (result as any)?.message || `${selectedRoleIds.length} role(s) assigned successfully`
      toast.success(message)

      setCurrentPage(1)
      await handleRefresh() // Use handleRefresh instead of fetchData
      setIsAddModalOpen(false)
      setSelectedRoleIds([])
    } catch (error: any) {
      console.error("handleBulkAssignRoles error:", error)
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to assign roles"
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  

  // Handle single role removal
  const handleRemoveRole = async (userRole: UserRole) => {
    const result = await Swal.fire({
      title: "Remove Role?",
      text: `Remove "${userRole.role_name}" from this user?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, remove it!",
      reverseButtons: true,
    })

    if (!result.isConfirmed) return

    try {
      await UserRoleService.delete(userRole.id)
      toast.success("Role removed successfully")

      // Adjust pagination if needed
      if (assignedRoles.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1)
      } else {
        await handleRefresh() // Use handleRefresh instead of fetchData
      }
    } catch (error: any) {
      console.error("handleRemoveRole error:", error)
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to remove role"
      toast.error(errorMessage)
    }
  }

  // Handle pagination
  const handlePageChange = (page: number) => setCurrentPage(page)
  const handlePerRowsChange = (newPerPage: number) => {
    setPerPage(newPerPage)
    setCurrentPage(1)
  }

  // Handle role selection in modal
  const handleRoleSelection = (roleId: number, checked: boolean) => {
    if (checked) {
      setSelectedRoleIds((prev) => [...prev, roleId])
    } else {
      setSelectedRoleIds((prev) => prev.filter((id) => id !== roleId))
    }
  }

  // Filter assigned roles based on search
  const filteredAssignedRoles = assignedRoles.filter((userRole) => {
    if (!userRole) return false
    const roleNameMatch = userRole.role_name?.toLowerCase().includes(searchTerm.toLowerCase()) || false
    return roleNameMatch
  })

  // Table columns
  const columns: TableColumn<UserRole>[] = [
    {
      name: "Role",
      selector: (row: UserRole) => row.role_name || "",
      sortable: true,
      cell: (row: UserRole) => (
        <div>
          <div className="fw-large">{row.role_name || "Unknown Role"}</div>
          {/* <small className="text-muted">ID: {row.role}</small> */}
        </div>
      ),
      width: "30%",
    },
    
    {
      name: "Assigned",
      selector: (row: UserRole) => row.assigned_at,
      sortable: true,
      cell: (row: UserRole) => (
        <span>{row.assigned_at ? new Date(row.assigned_at).toLocaleDateString() : "Unknown"}</span>
      ),
      width: "15%",
    },
    {
      name: "Assigned By",
      selector: (row: UserRole) => row.created_by || "",
      cell: (row: UserRole) => <span className="text-muted">{row.created_by || "Superuser"}</span>,
      width: "20%",
    },
    {
      name: "Actions",
      cell: (row: UserRole) => (
        <div className="d-flex gap-1">
          
          <Button
            color="danger"
            size="xs"
            onClick={() => handleRemoveRole(row)}
            title="Remove role"
            className="btn-icon"
          >
            <FaTrash size={12} />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "20%",
    },
  ]

  const getUserDisplayName = () => {
    if (userInfo) {
      return userInfo.full_name || userInfo.email || `User ${userId}`
    }
    return `User ${userId}`
  }

  const getUserEmail = () => {
    return userInfo?.email || "Loading..."
  }

  return (
    <div className="container-fluid">
      {/* Header Actions */}
      <Row className="mb-3">
        <Col md="6">
          <Button color="secondary" onClick={onNavigateBack}>
            <FaArrowLeft className="me-2" /> Back to Person
          </Button>
        </Col>
        <Col md="6" className="text-end">
          <Button color="success" className="me-2" onClick={handleRefresh} disabled={loading}>
            <FaSync className="me-2" /> Refresh
          </Button>
          <Button color="primary" onClick={() => setIsAddModalOpen(true)} disabled={availableRoles.length === 0}>
            <FaPlus className="me-2" /> Assign Roles
          </Button>
        </Col>
      </Row>

      {/* User Info Card */}
      <Card className="mb-3">
        <CardBody>
          <Row>
            <Col md="8">
              <div className="d-flex align-items-center">
                <div
                  className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{ width: "50px", height: "50px" }}
                >
                  <FaUser size={20} />
                </div>
                <div>
                  <h5 className="mb-1">{getUserDisplayName()}</h5>
                  <small className="text-muted">{getUserEmail()}</small>
                </div>
              </div>
            </Col>
            <Col md="4">
              {stats && (
                <div className="text-end">
                  <div className="d-flex justify-content-end gap-2">
                    
                    <Badge color="secondary" pill className="px-3 py-2">
                      {stats.total_roles || 0} Total
                    </Badge>
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert color="danger" className="mb-3">
          <strong>Error:</strong> {error}
          <Button color="outline-danger" size="sm" className="ms-2" onClick={handleRefresh}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" size="lg" />
          <div className="mt-3">Loading assigned roles...</div>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <CardTitle tag="h5" className="mb-1">
                  Role Assignments for {getUserDisplayName()}
                </CardTitle>
                <small className="text-muted">Manage roles assigned to this user</small>
              </div>
              <div className="text-end">
                <Badge color="info" className="me-2">
                  {totalRows} assigned
                </Badge>
                <Badge color="secondary">{availableRoles.length} available</Badge>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {/* Search */}
            <div className="mb-3">
              <div className="position-relative">
                <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <Input
                  type="text"
                  className="ps-5"
                  placeholder="Search assigned roles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Data Table */}
            <DataTable
              columns={columns}
              data={filteredAssignedRoles}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={perPage}
              paginationDefaultPage={currentPage}
              paginationRowsPerPageOptions={[10, 25, 50]}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              highlightOnHover
              striped
              noDataComponent={
                <div className="text-center py-4">
                  <div className="text-muted">No roles assigned to this user</div>
                  <Button
                    color="primary"
                    size="sm"
                    className="mt-2"
                    onClick={() => setIsAddModalOpen(true)}
                    disabled={availableRoles.length === 0}
                  >
                    Assign First Role
                  </Button>
                </div>
              }
              dense
              responsive
            />
          </CardBody>
        </Card>
      )}

      {/* Bulk Assignment Modal */}
      <Modal isOpen={isAddModalOpen} toggle={() => setIsAddModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setIsAddModalOpen(false)}>Assign Roles to {getUserDisplayName()}</ModalHeader>
        <ModalBody>
          {availableRoles.length === 0 ? (
            <Alert color="info">All available roles are already assigned to this user.</Alert>
          ) : (
            <>
              <div className="mb-3">
                <Label>Select roles to assign ({selectedRoleIds.length} selected):</Label>
              </div>
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {availableRoles.map((role) => (
                  <FormGroup check key={role.id} className="mb-2">
                    <Input
                      type="checkbox"
                      id={`role-${role.id}`}
                      checked={selectedRoleIds.includes(role.id)}
                      onChange={(e) => handleRoleSelection(role.id, e.target.checked)}
                    />
                    <Label check htmlFor={`role-${role.id}`} className="ms-2">
                      <div>
                        <strong>{role.name}</strong>
                        <Badge color={role.is_active ? "success" : "secondary"} className="ms-2 small">
                          {role.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      {role.description && <small className="text-muted">{role.description}</small>}
                    </Label>
                  </FormGroup>
                ))}
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsAddModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleBulkAssignRoles} disabled={selectedRoleIds.length === 0 || saving}>
            {saving ? <Spinner size="sm" className="me-2" /> : null}
            Assign {selectedRoleIds.length} Role{selectedRoleIds.length !== 1 ? "s" : ""}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
